import { FC, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

import {
    Alert,
    Box,
    ButtonDropdown,
    Container,
    ContentLayout,
    Grid,
    Header,
    SpaceBetween
} from '@cloudscape-design/components'

import HeaderWithStatusBadges from '../../../components/Common/HeaderWithStatusBadges'
import LoadingSpinner from '../../../components/LoadingSpinner'
import ApprovalForm from '../../../components/gcpProject/ApprovalForm'

import useSingleGcpProject from '../../../hooks/query/useSingleGcpProject'
import { KeyValuePairs } from '@aws-northstar/ui'

import GcpProjectApprovalDetails from '../../../components/gcpProject/ApprovalDetails'
import RejectionForm from '../../../components/gcpProject/RejectionForm'
import GcpProjectSubnetDetails from '../../../components/gcpProject/SubnetDetails'

import EditGenericTags from '../../../components/ControlledFormInputs/GenericTagsEditGCP'
import ReadGenericTagsGCP from '../../../components/ControlledFormInputs/GenericTagsReadGCP'
import RequiredPermissions from '../../../helper/RequiredPermissions'
import CONSTANTS from '../../../constants'

import useUserPermissions from '../../../hooks/query/useUserPermissions'
import { useApprovalFormStore } from '../../../stores/gcpProjects/approvalFormStore'
import type { TagsApproval } from '../../../types/tags'
import hasAllPermissions from '../../../helper/hasRequiredPermissions'
import getPlatformOwnerPermissions from '../../../helper/platformOwnerPermissions'
const GcpProjectDetails: FC = () => {
    const CURRENT_USER_ID = JSON.parse(localStorage["okta-token-storage"])["idToken"]["claims"]["email"]
    const { data: userPermissions } = useUserPermissions(CURRENT_USER_ID);
    const { id: gcpProjectID } = useParams() as { id: string }
    const hasSpecificPermissions = (permissions: string[], requiredPermissions: string[]): boolean => {
        return requiredPermissions.some(requiredPerm => permissions.includes(requiredPerm));
    };
    const specificPermissions = ["gcpProjects:*:*", "gcpProjects:*:approve", "*:*:*"];
    const hasRequiredPermissions = userPermissions && hasSpecificPermissions(userPermissions.permissions, specificPermissions);
    const platformOwnerResource = { type: 'gcpProjects' as const, id: gcpProjectID }
    const hasPlatformOwnerPermissions = Boolean(
        userPermissions && hasAllPermissions(
            getPlatformOwnerPermissions(platformOwnerResource),
            userPermissions.permissions
        )
    )
    const canReadGcpProjectTags = Boolean(
        userPermissions && (hasAllPermissions(
            CONSTANTS.GCPPROJECT_REQUESTOR,
            userPermissions.permissions
        ) || hasPlatformOwnerPermissions)
    )
    const { data: thisgcpProject, isLoading } = useSingleGcpProject(gcpProjectID, true)
    const {
        actions: { clearFormValues }
    } = useApprovalFormStore()

    const [showApprovalForm, setShowApprovalForm] = useState(false)
    const [showRejectionForm, setShowRejectionForm] = useState(false)

    useEffect(() => {
        clearFormValues()
    }, [clearFormValues, gcpProjectID])


    if (!thisgcpProject || isLoading) {
        return (
            <ContentLayout
                header={
                    <HeaderWithStatusBadges
                        headerName=""
                        isLoadingApprovals
                        skeletonBadgeCount={4}
                    />
                }
            >
                <LoadingSpinner />
            </ContentLayout>
        )
    }

    if (thisgcpProject.status === "ERROR") {
        return (
            <Alert
                statusIconAriaLabel="Error"
                type="error"
                header="An error occurred">
                {thisgcpProject.details.message}
            </Alert>
        );
    }

    const isSandboxProject = thisgcpProject?.type === 'Sandbox'

    const keyValuePairsItems = [
        [
            {
                label: 'Requestor ID',
                value: thisgcpProject?.requestorID
            },
            {
                label: 'Project Type',
                value: thisgcpProject?.type
            },
            ...(!isSandboxProject
                ? [
                    {
                        label: 'Infrastructure Type',
                        value: thisgcpProject?.details.infrastructureType || 'NA'
                    }
                ]
                : []),
            {
                label: 'Budget Threshold Alert',
                value: thisgcpProject?.details.monthlyBudgetCap
            }
        ],
        [
            {
                label: 'Project Viewer Distribution List',
                value: thisgcpProject?.details.projectViewerDistributionList
            },
            {
                label: 'Budget Alert Distribution List',
                value: thisgcpProject?.details.budgetAlertsDistributionList
            }
        ]
    ]

    const shouldShowSandboxSchedule = isSandboxProject

    const nukerPipelineDetails = shouldShowSandboxSchedule
        ? [
            [
                {
                    label: 'Cron Timezone',
                    value: thisgcpProject?.details?.cronTimezone
                }
            ],
            [
                {
                    label: 'Cron Syntax',
                    value: thisgcpProject?.details?.cronSyntax
                }
            ]
        ]
        : []

    const isAllTagsApproved =
        thisgcpProject?.tags?.approvalStatus?.every(
            (tagStatus: TagsApproval) => tagStatus.status === 1
        ) ?? false

    const canShowActionButton =
        thisgcpProject.status === 'PENDING_APPROVAL' &&
        CURRENT_USER_ID !== thisgcpProject.requestorID

    return (
        <ContentLayout
            header={
                <HeaderWithStatusBadges
                    headerName={thisgcpProject?.details.projectName}
                    status={thisgcpProject?.status}
                    approvalStatus={thisgcpProject?.tags?.approvalStatus || []}
                />
            }
        >
            <Grid
                gridDefinition={[
                    { colspan: { default: 3, xxs: 9 } },
                    { colspan: { default: 9, xxs: 3 } }
                ]}
            >
                <SpaceBetween direction="vertical" size="l">
                    <Container header={<Header variant="h3">Request Details</Header>}>
                        <KeyValuePairs items={keyValuePairsItems} />
                    </Container>
                    {shouldShowSandboxSchedule && (
                        <Container
                            header={<Header variant="h3">Resource Nuker Pipeline Details</Header>}
                        >
                            <KeyValuePairs items={nukerPipelineDetails} />
                        </Container>
                    )}
                    {!hasRequiredPermissions &&
                        thisgcpProject.status === 'PENDING_APPROVAL' &&
                        canReadGcpProjectTags && (
                            <Container header={<Header variant="h3">Tags Details</Header>}>
                                {thisgcpProject.tagsID && (
                                    <ReadGenericTagsGCP
                                        tagsId={thisgcpProject.tagsID}
                                        platformOwnerResource={platformOwnerResource}
                                        allowPlatformOwnerApproval={true}
                                        platformOwnerEmail={
                                            thisgcpProject?.details?.[thisgcpProject?.type]
                                                ?.platformOwnerEmail ||
                                            thisgcpProject?.details?.platformOwnerEmail
                                        }
                                    />
                                )}
                            </Container>
                    )}
                    {(
                        thisgcpProject.status === 'APPROVED' ||
                        thisgcpProject.status === 'REJECTED') && (
                        <RequiredPermissions
                            requiredPermissions={CONSTANTS.GCPPROJECT_REQUESTOR}
                        >
                            <Container header={<Header variant="h3">Tags Details</Header>}>
                                {thisgcpProject.tagsID && (
                                    <ReadGenericTagsGCP
                                        tagsId={thisgcpProject.tagsID}
                                    />
                                )}
                            </Container>
                        </RequiredPermissions>
                    )}
                    {thisgcpProject.status === 'PENDING_APPROVAL' && hasRequiredPermissions && (
                        <RequiredPermissions
                            requiredPermissions={CONSTANTS.GCPPROJECT_APPROVER}
                        // requiredPermissions={[...CONSTANTS.GCPPROJECT_APPROVER,`gcpProjects:${gcpProjectID}:read`, `gcpProjects:${gcpProjectID}:approve`, `gcpProjects:${gcpProjectID}:update`]}
                        >
                            <Container header={<Header variant="h3">Tags Details</Header>}>
                                {thisgcpProject.tagsID && (
                                    <EditGenericTags
                                        tagsId={thisgcpProject.tagsID}
                                    />
                                )}
                            </Container>
                        </RequiredPermissions>
                    )}
                    {thisgcpProject.status === 'APPROVED' && !isSandboxProject && (
                        <GcpProjectSubnetDetails gcpProject={thisgcpProject} />
                    )}
                    {canShowActionButton && (
                            <RequiredPermissions
                                requiredPermissions={CONSTANTS.GCPPROJECT_APPROVER}
                            // requiredPermissions={[`gcpProjects:${gcpProjectID}:read`, `gcpProjects:${gcpProjectID}:approve`, `gcpProjects:${gcpProjectID}:update`]}
                            >
                                <Box float="right">
                                    <ButtonDropdown
                                        items={isAllTagsApproved ? [
                                            { text: 'To Approve...', id: 'to-approve' },
                                            { text: 'To Reject...', id: 'to-reject' }
                                        ] : [
                                            { text: 'To Reject...', id: 'to-reject' }
                                        ]}
                                        onItemClick={({ detail }) => {
                                            if (detail.id === 'to-approve') {
                                                setShowApprovalForm(true)
                                                setShowRejectionForm(false)
                                            }

                                            if (detail.id === 'to-reject') {
                                                setShowApprovalForm(false)
                                                setShowRejectionForm(true)
                                            }
                                        }}
                                        variant="primary"
                                    >
                                        Action
                                    </ButtonDropdown>
                                </Box>
                            </RequiredPermissions>
                        )}

                    {showApprovalForm && (
                        <ApprovalForm
                            thisgcpProjectID={thisgcpProject.gcpProjectID}
                            shouldShowApprovalForm={setShowApprovalForm}
                        />
                    )}

                    {showRejectionForm && (
                        <RejectionForm
                            thisgcpProjectID={thisgcpProject.gcpProjectID}
                            shouldShowRejectionForm={setShowRejectionForm}
                        />
                    )}

                    {thisgcpProject.status === 'APPROVED' && (
                        <GcpProjectApprovalDetails
                            gcpProjectApprovalDetails={thisgcpProject.approval!}
                        />
                    )}
                </SpaceBetween>
            </Grid>
        </ContentLayout>
    )
}

export default GcpProjectDetails
